import { useAuthModal, useAuthStore } from '@/features/auth/store'
import { i18n } from '@/locales'
import { useFairToast } from '@/ui/patterns/FairToast'
import {
  confirmEmailChange,
  toEmailLinkError,
  turnOffAlert,
  unsubscribeGroup,
  EmailLinkError,
} from './api/emailLinks'
import { useEmailLinkStore } from './store'

/**
 * Route handlers for the links in FAIR emails (`emails/`, BE-210). Each one runs
 * the action from the browser, leaves the result in the store and opens the
 * matching screen of the auth modal over Home; the route then redirects to
 * Home, which also keeps the token out of the address bar.
 */

function showLinkProblem(error: unknown): void {
  const reason = error instanceof EmailLinkError ? error.reason : 'unknown'
  useFairToast().show({
    message: i18n.global.t(reason === 'unknown' ? 'email_links.errors.generic' : 'email_links.errors.invalid_link'),
    tone: 'warning',
  })
}

/** Email 01 → Email verified: success / link expired / already confirmed. */
export async function handleEmailVerifyLink(token: string): Promise<void> {
  const links = useEmailLinkStore()
  try {
    await useAuthStore().verifyUser(token)
    links.verifiedStatus = 'success'
  } catch (error) {
    const { reason } = toEmailLinkError(error)
    if (reason === 'unknown') {
      showLinkProblem(error)
      return
    }
    links.verifiedStatus = reason
  }
  useAuthModal().showAuthModal('emailVerified')
}

/** Email 04 → Email updated: success / link expired. */
export async function handleEmailChangeLink(token: string): Promise<void> {
  const links = useEmailLinkStore()
  try {
    const { email } = await confirmEmailChange(token)
    links.updatedStatus = 'success'
    links.updatedEmail = email
  } catch (error) {
    if (error instanceof EmailLinkError && error.reason === 'unknown') {
      showLinkProblem(error)
      return
    }
    // "Already used" also means the change went through earlier — but the
    // address is unknown here, so it lands on the calm expired copy.
    links.updatedStatus = 'expired'
    links.updatedEmail = ''
  }
  useAuthModal().showAuthModal('emailUpdated')
}

/** Emails 03 / 05 → Secure your account. Nothing runs until the form is sent. */
export function handleSecureAccountLink(token: string): void {
  useEmailLinkStore().secureToken = token
  useAuthModal().showAuthModal('secureAccount')
}

/** Marketing footer "Unsubscribe" → one click, no sign-in, that group only. */
export async function handleUnsubscribeLink(token: string): Promise<void> {
  const links = useEmailLinkStore()
  try {
    const { group } = await unsubscribeGroup(token)
    links.unsubscribeToken = token
    links.unsubscribedGroup = group
    useAuthModal().showAuthModal('unsubscribed')
  } catch (error) {
    showLinkProblem(error)
  }
}

/** Alert footer "Turn off this alert" → works for guests too (BE-117 token). */
export async function handleAlertOffLink(token: string): Promise<void> {
  const links = useEmailLinkStore()
  try {
    const { productUrl } = await turnOffAlert(token)
    links.alertToken = token
    links.alertProductUrl = productUrl
    useAuthModal().showAuthModal('alertTurnedOff')
  } catch (error) {
    showLinkProblem(error)
  }
}
